// THE DROP OUTCOME: "If an upload only contains files that can be processed in real time, then just
// show a 'success' screen after they're uploaded. Otherwise, instead of saying 'processing, may
// take up to ##' it should list the files it's handing off for deeper analysis and say to check
// back later.
import type { BoundaryScanResult } from "../../lib/boundaryScan";
import type { FastPathReport } from "../../lib/bulkFieldFastPath";
import type { SampleAttachReport } from "../../lib/sampleFastPath";
import type {
  ZoneAttachReport,
  ZoneResultAttachReport,
} from "../../lib/zoneFastPath";
import type { DropIdentification, IdentifiedKind } from "./dropIdentify";

export interface HandoffEntry {
  file: string;
  reasons: string[];
}

const KIND_REASON: Partial<Record<IdentifiedKind, string>> = {
  zones: "zone set — processed by the server",
  yield: "yield map — processed as machine data",
  ec: "EC scan — processed by the server",
  "samples-noresults":
    "sample points without lab results — processed by the server",
  unidentified: "unidentified — processing will classify it",
  polygons: "polygons — processing will classify them",
};

export function handoffFor(input: {
  scan: BoundaryScanResult | null;
  fastReport: FastPathReport | null;
  sampleReport: SampleAttachReport | null;
  sampleLeft: { file: string; reason: string }[];
  ident: DropIdentification;
  /* * the sample opt-in — off means the server IGNORES sample files, which the user should hear */
  maySamples: boolean;
  /*
   * files the yield lane actually FILED (got a `yield_job_id` and uploaded). Those are handled-fast
   * not "handed off for deeper analysis". A scan without a job id stays on the handoff list.
   */
  attestedYieldFiles?: ReadonlySet<string>;
  zoneReport?: ZoneAttachReport | null;
  zoneResultReport?: ZoneResultAttachReport | null;
  zoneLeft?: { file: string; reason: string }[];
  /* * files a sync lane already saved or refused — not "handed to the server" */
  alsoHandled?: readonly string[];
}): HandoffEntry[] {
  const out = new Map<string, string[]>();
  const add = (file: string, reason: string) => {
    const top = file.split("/")[0]!;
    const list = out.get(top) ?? [];
    if (!list.includes(reason)) list.push(reason);
    out.set(top, list);
  };

  const attachedFiles = new Set(
    (input.sampleReport?.attached ?? []).map((a) => a.file),
  );
  const leftFiles = new Set(input.sampleLeft.map((l) => l.file));
  for (const a of input.zoneReport?.attached ?? []) attachedFiles.add(a.file);
  for (const a of input.zoneResultReport?.attached ?? [])
    attachedFiles.add(a.file);
  const zoneLeftFiles = new Set(
    (input.zoneLeft ?? []).map((l) => l.file.split("/")[0]!),
  );
  const candidateFiles = new Set(
    (input.scan?.candidates ?? []).map((c) => c.sourceFile.split("/")[0]!),
  );
  const handledNames = new Set(input.alsoHandled ?? []);

  // boundaries the scan refused (CSVs/others are "not a boundary-carrying format" — other lanes speak for them)
  for (const s of input.scan?.skipped ?? []) {
    if (s.reason === "not a boundary-carrying format") continue;
    const top = s.file.split("/")[0]!;
    if (
      attachedFiles.has(top) ||
      attachedFiles.has(s.file) ||
      zoneLeftFiles.has(top)
    )
      continue;
    add(s.file, `boundary — ${s.reason}`);
  }
  // candidates the fast path could not create, or never tried (over the per-drop cap)
  if (input.fastReport) {
    for (const f of input.fastReport.failed)
      add(
        f.candidate.sourceFile,
        `${f.candidate.name} — the server will create it`,
      );
  } else {
    for (const c of input.scan?.candidates ?? [])
      add(c.sourceFile, `${c.name} — the server will create it`);
  }
  // sample files the lane refused (incl. pairing notes and incomplete pairs), with their reasons
  for (const l of input.sampleLeft) {
    const top = l.file.split("/")[0]!;
    if (attachedFiles.has(l.file) || attachedFiles.has(top)) continue;
    add(l.file, l.reason);
  }
  for (const l of input.zoneLeft ?? []) {
    const top = l.file.split("/")[0]!;
    if (attachedFiles.has(l.file) || attachedFiles.has(top)) continue;
    add(l.file, l.reason);
  }

  for (const i of input.ident.items) {
    if (handledNames.has(i.file) || handledNames.has(i.file.split("/")[0]!))
      continue;
    if (i.kind === "samples-results") {
      if (attachedFiles.has(i.file) || leftFiles.has(i.file)) continue;
      add(
        i.file,
        input.maySamples
          ? "sample points with lab results — attached during processing"
          : "sample points with lab results — NOT imported: the soil-samples option was off",
      );
      continue;
    }
    if (i.kind === "polygons" && candidateFiles.has(i.file)) continue; // the Boundaries row told its story
    if (i.kind === "yield" && input.attestedYieldFiles?.has(i.file)) continue;
    if (
      (i.kind === "zones" || i.kind === "samples-noresults") &&
      (attachedFiles.has(i.file) || zoneLeftFiles.has(i.file))
    )
      continue;
    const reason = KIND_REASON[i.kind];
    if (reason) add(i.file, reason);
  }
  return [...out.entries()].map(([file, reasons]) => ({ file, reasons }));
}
